import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import InternalSidebar from '../../components/InternalSidebar';
import InternalTopMenu from '../../components/InternalTopMenu';
import EmailVerificationBanner from '../../components/EmailVerificationBanner';

const MAX_PIX_KEYS = 6;

const PixList = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [pixKeys, setPixKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!user?.id) return;

    const fetchKeys = async () => {
      try {
        setLoading(true);
        setError('');
        setSuccess('');
        const response = await api.get(`/bank/pix/${user.id}`);
        setPixKeys(response.data?.keys || []);
      } catch (err) {
        setError(
          err.response?.data?.message ||
            err.message ||
            'Não foi possível carregar suas chaves PIX.'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchKeys();
  }, [user?.id]);

  const keys = useMemo(() => {
    return pixKeys.map((item, index) => {
      const uuid = item.uuid || item.Uuid || `${user?.id}-${index}`;
      const createdAtData = item.createdAt || item.CreatedAt || {};
      const createdAtFormatted = createdAtData.formatted || createdAtData.Formatted;
      
      let formattedDate = '—';
      if (createdAtFormatted && createdAtFormatted !== '-') {
        formattedDate = createdAtFormatted;
      } else if (createdAtData.raw) {
        try {
          const date = new Date(createdAtData.raw);
          if (!Number.isNaN(date.getTime())) {
            formattedDate = new Intl.DateTimeFormat('pt-BR', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            }).format(date);
          }
        } catch (e) {
          // Keep default '—'
        }
      }
      
      return {
        id: uuid,
        uuid,
        pixKey: item.pixKey || item.PixKey || '—',
        createdAt: formattedDate,
      };
    });
  }, [pixKeys, user?.id]);

  const hasKeys = keys.length > 0;
  const hasReachedLimit = keys.length >= MAX_PIX_KEYS;

  const handleDelete = async (uuid) => {
    if (!uuid) return;

    const confirmed = window.confirm('Deseja realmente remover esta chave PIX?');
    if (!confirmed) return;

    try {
      setError('');
      setSuccess('');
      await api.delete(`/bank/pix/${uuid}`);
      setPixKeys((prev) => prev.filter((key) => (key.uuid || key.Uuid) !== uuid));
      setSuccess('Chave PIX removida com sucesso.');
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Não foi possível remover a chave PIX.';
      setError(message);
    }
  };

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  return (
    <div className="min-h-screen bg-[#f7f7f7] text-slate-700">
      <div className="min-h-screen flex flex-col lg:flex-row">
        <div className="lg:hidden">
          <div className="flex items-center justify-between px-6 py-4 bg-white shadow-sm">
            <button
              type="button"
              className="text-2xl text-slate-700"
              onClick={toggleSidebar}
              aria-label="Abrir menu"
            >
              <i className="bi-list"></i>
            </button>
            <img src="/img/branded/logob.png" alt="Stackfy" className="h-8" />
            <div className="w-7" />
          </div>
        </div>

        <InternalSidebar active="account-section" open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        {sidebarOpen && (
          <button
            type="button"
            className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-label="Fechar menu"
          ></button>
        )}

        <main className="flex-1 px-6 py-8 lg:px-10 lg:py-12">
          <EmailVerificationBanner />
          <header className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <button
              type="button"
              onClick={() => navigate('/account')}
              className="text-sm font-semibold flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
            >
              <i className="bi-arrow-left-short text-xl"></i> Voltar
            </button>

            <InternalTopMenu onLogout={logout} />
          </header>

          <section className="rounded-3xl border border-slate-200 bg-white px-6 py-6 shadow-sm max-w-3xl mx-auto">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <div>
                <h1 className="text-xl font-semibold text-slate-900">Chaves PIX</h1>
                <p className="text-sm text-slate-500">Gerencie as chaves utilizadas para transferências instantâneas</p>
              </div>
              <button
                type="button"
                onClick={() => navigate('/bank/pix/add')}
                disabled={hasReachedLimit}
                className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-xs sm:text-sm font-semibold transition-colors ${
                  hasReachedLimit
                    ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                    : 'bg-tBlue text-white hover:bg-tBlue2'
                }`}
                title={hasReachedLimit ? `Limite máximo de ${MAX_PIX_KEYS} chaves PIX atingido` : ''}
              >
                <i className="bi-plus-circle"></i> Adicionar PIX
              </button>
            </div>

            {loading && <div className="text-sm text-gray-500">Carregando chaves PIX...</div>}

            {error && !loading ? (
              <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>
            ) : null}

            {success && !loading ? (
              <div className="rounded border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                {success}
              </div>
            ) : null}

            {!loading && !error && !hasKeys ? (
              <div className="flex flex-col items-center justify-center text-center py-16 gap-4">
                <img src="/img/payment.webp" alt="" className="w-24 h-auto opacity-80" />
                <div>
                  <h2 className="text-base font-semibold text-slate-800">Nenhuma chave cadastrada</h2>
                  <p className="text-sm text-slate-500">Adicione uma chave PIX para receber transferências com rapidez.</p>
                </div>
              </div>
            ) : null}

            {hasReachedLimit && !loading && (
              <div className="mb-4 rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                Você atingiu o limite máximo de {MAX_PIX_KEYS} chaves PIX. Remova uma chave para adicionar uma nova.
              </div>
            )}

            {hasKeys && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs md:text-sm">
                  <thead className="text-[10px] uppercase tracking-wide text-slate-400">
                    <tr>
                      <th className="py-3 text-left font-semibold">Chave PIX</th>
                      <th className="py-3 text-left font-semibold">Criada em</th>
                      <th className="py-3 text-left font-semibold">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-600">
                    {keys.map((row) => (
                      <tr key={row.id} className="hover:bg-slate-50 transition-colors text-[11px] md:text-xs">
                        <td className="py-3 pr-3">{row.pixKey}</td>
                        <td className="py-3 pr-3">{row.createdAt}</td>
                        <td className="py-3 pr-3">
                          <button
                            type="button"
                            onClick={() => handleDelete(row.uuid)}
                            className="text-xs font-semibold text-red-500 hover:text-red-600 transition-colors"
                          >
                            Deletar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </main>
      </div>
    </div>
  );
};

export default PixList;

